import * as React from "react";
import { PERMISSIONS } from "@/constants";
import { Box, Card, LinearProgress, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import { provingGroundKeys, RunSummary } from "@/hooks/api/use-proving-ground";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useAuth } from "@/components/core/auth-provider";
import { CustomTable } from "@/components/customTable";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { MONO, standingOf, verdictInk } from "../shared";
import { LaunchRunDialog } from "./launch-dialog";

/** Same treatment as the run detail page, so the two screens read as one instrument. */
const FIGURE_SX = {
	fontFamily: MONO,
	fontSize: 13,
	fontWeight: 600,
	fontVariantNumeric: "tabular-nums" as const,
};

const TERM_SX = {
	fontFamily: MONO,
	fontSize: 11,
	fontWeight: 600,
	letterSpacing: ".05em",
	textTransform: "uppercase" as const,
};

const RUN_STATUS_OPTIONS = [
	{ label: "Queued", value: "queued" },
	{ label: "Running", value: "running" },
	{ label: "Completed", value: "completed" },
	{ label: "Failed", value: "failed" },
	{ label: "Cancelled", value: "cancelled" },
];

const RUN_MODE_OPTIONS = [
	{ label: "Voice", value: "voice" },
	{ label: "Text", value: "text" },
];

function statusInk(status: string) {
	if (status === "failed") return verdictInk("gate");
	if (status === "completed") return { color: "text.primary" };
	return { color: "text.secondary" };
}

/** `.main` measures 2.2–3.8:1 as text on a light card, so the verdict ink is used instead. */
function rateInk(passed: number, total: number) {
	return verdictInk(standingOf(passed, total - passed));
}

export function ProvingGroundRuns(): React.JSX.Element {
	const navigate = useNavigate();
	const { permissions } = useAuth();
	const [launchOpen, setLaunchOpen] = React.useState(false);

	/**
	 * Whether this reader configures the Proving Ground or only reads published runs.
	 *
	 * A manager holds `provingRun` and not `agent`, which is exactly the distinction publishing
	 * was built on. Everything below that touches the libraries — the agent filter, the launch
	 * button and the dialog behind it — is theirs only if they have it.
	 */
	const canLaunch = Boolean(permissions[PERMISSIONS.agent]);

	const {
		items: runs,
		isLoading,
		totalItems,
		refetchData,
	} = useCachedTableStateWithUrl<RunSummary>({
		queryKey: provingGroundKeys.runs(),
		endpoint: "/proving-ground/runs",
	});

	// A run in flight changes underneath the list, so the table refreshes itself rather than
	// leaving a queued run looking stuck until someone reloads.
	React.useEffect(() => {
		const active = runs.some((run) => run.status === "queued" || run.status === "running");
		if (!active) return;
		const timer = setInterval(() => refetchData(), 4000);
		return () => clearInterval(timer);
	}, [runs, refetchData]);

	// `name` is the query parameter the API receives - there is no mapping layer, so these
	// have to match RunsFilterQuery exactly. The empty dependency array is load-bearing:
	// CustomTable seeds its filter state from this array once and reads keys off it
	// unguarded afterwards, so an identity change mid-life throws.
	const filtersOptions: FilterOption[] = React.useMemo(
		() => [
			// Offered only to a reader who can read /agents. For a manager this filter would fetch
			// the agent list on mount and answer 403, which is one of the toasts that greeted them
			// on a page they are entitled to be on.
			...(canLaunch
				? [
						{
							name: "agentId",
							label: "Agent",
							value: "",
							type: FilterType.FETCH_SELECT,
							getOptionsUrl: "/agents",
							fetchLabel: "name",
							fetchValue: "id",
						} as FilterOption,
					]
				: []),
			{ name: "status", label: "Status", value: [], type: FilterType.MULTI_SELECT, options: RUN_STATUS_OPTIONS },
			{ name: "mode", label: "Mode", value: "", type: FilterType.SELECT, options: RUN_MODE_OPTIONS },
			{ name: "createdAtFrom", label: "Started from", value: "", type: FilterType.DATE },
			{ name: "createdAtTo", label: "Started to", value: "", type: FilterType.DATE },
		],
		[canLaunch]
	);

	const columns: Column[] = React.useMemo(
		() => [
			{
				header: { id: "id", label: "Run", align: "left", sortBy: "id", sx: { fontWeight: "600" } },
				accessorKey: "id",
				getCellValue: (row: any) => (
					<Tooltip title="Open run" arrow>
						<Box
							tabIndex={0}
							onClick={() => navigate(`/proving-ground/runs/${row.id}`)}
							sx={{ ...FIGURE_SX, cursor: "pointer", width: "max-content", "&:hover": { textDecoration: "underline" } }}
						>
							#{row.id}
						</Box>
					</Tooltip>
				),
			},
			{
				header: { id: "agentName", label: "Agent", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "agentName",
			},
			{
				header: { id: "mode", label: "Mode", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "mode",
				getCellValue: (row: any) => (
					<Typography sx={[TERM_SX, { color: row.mode === "voice" ? "text.primary" : "text.secondary" }]}>
						{row.mode}
					</Typography>
				),
			},
			{
				header: { id: "status", label: "Status", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "status",
				getCellValue: (row: any) => (
					<Stack spacing={0.5}>
						<Typography sx={[TERM_SX, statusInk(row.status)]}>{row.status}</Typography>
						{row.status === "running" || row.status === "queued" ? (
							<Box sx={{ minWidth: 90 }}>
								<LinearProgress
									variant="determinate"
									value={row.totalCells ? (row.completedCells / row.totalCells) * 100 : 0}
									sx={{ height: 3, borderRadius: 3 }}
								/>
							</Box>
						) : null}
					</Stack>
				),
			},
			{
				header: { id: "passRate", label: "Pass rate", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "passedChecks",
				getCellValue: (row: any) => (
					<Typography sx={[FIGURE_SX, rateInk(row.passedChecks, row.totalChecks)]}>
						{row.totalChecks ? `${Math.round((row.passedChecks / row.totalChecks) * 100)}%` : "—"}
					</Typography>
				),
			},
			{
				header: { id: "checks", label: "Checks", align: "left", sortBy: "totalChecks", sx: { fontWeight: "600" } },
				accessorKey: "totalChecks",
				getCellValue: (row: any) => (
					<Typography sx={FIGURE_SX}>
						{row.passedChecks}
						<Box component="span" sx={{ color: "text.secondary" }}>
							/{row.totalChecks}
						</Box>
					</Typography>
				),
			},
			{
				header: { id: "totalCells", label: "Calls", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "totalCells",
				getCellValue: (row: any) => (
					<Typography sx={FIGURE_SX}>
						{row.completedCells}
						<Box component="span" sx={{ color: "text.secondary" }}>
							/{row.totalCells}
						</Box>
					</Typography>
				),
			},
			{
				header: { id: "spend", label: "Spend", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "spendUsd",
				getCellValue: (row: any) => {
					// Absent, not null: the server omits cost entirely for a reader who may not see
					// it, so `=== null` was false and the cell reached `undefined.toFixed` — which,
					// with no error boundary anywhere in the app, took the whole page down.
					if (row.spendUsd === undefined) {
						return <Typography sx={[FIGURE_SX, { color: "text.secondary" }]}>—</Typography>;
					}
					return (
						<Tooltip
							arrow
							title={
								row.spendUsd === null
									? "Set rates in Proving Ground settings to see a figure."
									: `Covers ${(row.spendCovers ?? []).join(", ")}.`
							}
						>
							{/* A partial figure is flagged in the same ink a partial verdict uses. */}
							<Typography
								sx={[FIGURE_SX, row.spendCovers?.length && row.spendCovers.length < 3 ? verdictInk("review") : {}]}
							>
								{row.spendUsd === null
									? `${(((row.inputTokens ?? 0) + (row.outputTokens ?? 0)) / 1000).toFixed(1)}k`
									: `$${row.spendUsd.toFixed(4)}`}
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				header: { id: "latency", label: "Latency p95", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "latencyP95Ms",
				getCellValue: (row: any) =>
					row.latencyP95Ms === null ? (
						<Typography sx={[FIGURE_SX, { color: "text.secondary" }]}>—</Typography>
					) : (
						<Tooltip
							arrow
							title={`Median ${row.latencyP50Ms}ms, worst ${row.latencyMaxMs}ms, over ${row.latencySamples} turns. From the caller falling silent to the agent starting to speak.`}
						>
							<Typography sx={FIGURE_SX}>{`${row.latencyP95Ms}ms`}</Typography>
						</Tooltip>
					),
			},
			{
				header: { id: "durationMs", label: "Duration", align: "left", sortBy: "durationMs", sx: { fontWeight: "600" } },
				accessorKey: "durationMs",
				getCellValue: (row: any) => (
					<Typography sx={FIGURE_SX}>{row.durationMs ? `${Math.round(row.durationMs / 1000)}s` : "—"}</Typography>
				),
			},
			{
				header: { id: "createdAt", label: "Started", align: "left", sortBy: "createdAt", sx: { fontWeight: "600" } },
				accessorKey: "createdAt",
				getCellValue: (row: any) => (
					<Typography variant="body2" color="text.secondary">
						{new Date(row.createdAt).toLocaleString()}
					</Typography>
				),
			},
		],
		[navigate]
	);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Proving Ground</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Put an agent through a grid of scenarios and personas, and score every call against your scorecard.
							</Typography>
						</Box>
						{canLaunch ? (
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button startIcon={<PlusIcon />} variant="contained" onClick={() => setLaunchOpen(true)}>
									Launch run
								</Button>
							</Box>
						) : null}
					</Stack>
					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={runs}
								refetchData={refetchData}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			{/* Mounted only for a reader who can launch. Its four hooks — agents, personas,
			    scenarios, metrics — fire on mount whether or not the dialog is open, and every one
			    of them is owner-only, so simply rendering it closed greeted a manager with a stack
			    of Forbidden toasts on a page they are entitled to be on. */}
			{canLaunch ? (
				<LaunchRunDialog
					open={launchOpen}
					onClose={() => setLaunchOpen(false)}
					onLaunched={(runId) => navigate(`/proving-ground/runs/${runId}`)}
				/>
			) : null}
		</React.Fragment>
	);
}
